import moment from "moment";
import { constants } from "../../constants/constants";
import { Modifiers, DayPicker, Matcher } from "react-day-picker";
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { ClickOutside } from "../common/ClickOutside";
import { CustomDatePicker } from "../common/date-range-custon-view/CustomDatePicker";
import IconSVG from "../../styles/svg-icons";

interface DayPickerInputProps {
    name?: string;
    className?: string;
    selectedDate?: Date;
    minimumDate?: Date;
    maximumDate?: Date;
    placeholder?: string;
    dateFormat?: string;
    disabled?: boolean;
    onDayChange: (day?: Date | string, dayRaw?: string) => void
    disabledDays?: Matcher[];
    allowWeekends?: boolean;
    onBlur?: (e: ChangeEvent) => void
    toMonth?: Date;
    emptyIfNull?: boolean;
    withCustomView?: boolean;
    dateInputFormat?: string[];
}

export const DayPickerInput = ({
    selectedDate,
    dateFormat = constants.dateFormat,
    dateInputFormat,
    className,
    disabledDays = [],
    minimumDate,
    maximumDate,
    onBlur,
    placeholder,
    disabled,
    toMonth,
    onDayChange,
    emptyIfNull = false,
    allowWeekends = true,
    withCustomView = false,
}: DayPickerInputProps
) => {
    const today = moment().startOf('day').toDate();
    const [month, setMonth] = useState<Date>(selectedDate ?? today);
    const [currentDate, setCurrentDate] = useState<Date>(selectedDate ?? today);
    const [showDayPicker, setShowDayPicker] = useState(false);

    const formatDate = useCallback((date: Date | undefined) =>
        date
            ? moment(date).startOf('day').format(dateFormat)
            : emptyIfNull ? '' : moment().startOf('day').format(dateFormat),
        [dateFormat, emptyIfNull]);

    const [inputValue, setInputValue] = useState(formatDate(selectedDate));

    useEffect(() => {
        const isSelectedDateChanged =
            !!selectedDate && (
                !inputValue ||
                !moment(inputValue, dateFormat, true).isValid() ||
                !moment(inputValue, dateFormat, true).isSame(selectedDate, 'day')
            );
        const isSelectedDateCleared = !selectedDate && !!inputValue;

        if (isSelectedDateChanged || isSelectedDateCleared) {
            setInputValue(formatDate(selectedDate));

            const today = moment().startOf('day').toDate();
            setCurrentDate(selectedDate ?? today);
            setMonth(selectedDate ?? today);
        } //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formatDate, selectedDate]);

    const handleOnDayClick = (date: Date, modifiers?: Modifiers) => {
        if (modifiers?.disabled) {
            return;
        }

        setCurrentDate(date);
        setInputValue(moment(date).format(dateFormat));
        onDayChange(date, moment(date).format());
        setShowDayPicker(false);
    };

    const handleRawInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value?.trim();
        const newCurrentMoment = dateInputFormat ? moment(value, dateInputFormat, true) : moment(value, dateFormat, true);

        if (newCurrentMoment.isValid()) {
            const date = newCurrentMoment.startOf('day').toDate();
            setCurrentDate(date);
            setMonth(date);
            onDayChange(date, moment(date).format(dateFormat));
            setInputValue(moment(date).format(dateFormat));
        } else if (!value) { // Empty input
            const date = moment().startOf('day').toDate();
            setCurrentDate(date);
            setMonth(date);
            onDayChange(undefined, '');
        } else {
            setInputValue(e.target.value);
        }
    }

    const handleRawInputBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value?.trim();

        if (value) {
            const newCurrentMoment = dateInputFormat
                ? moment(value, dateInputFormat, true)
                : moment(value, dateFormat, true);

            if (!newCurrentMoment.isValid()) {
                setInputValue(formatDate(selectedDate));
                const date = selectedDate ?? moment().startOf('day').toDate();
                setCurrentDate(date);
                setMonth(date);
                onDayChange(date, moment(date).format(dateFormat));
            }
        }

        onBlur?.(e);
    }

    const renderNavIcon = () => <IconSVG name="icon-expand" width={16} height={16} />;

    return (
        <ClickOutside onClick={() => setShowDayPicker(false)}>
            <div className="rdp-input">
                <input
                    className={className}
                    type="text"
                    placeholder={placeholder}
                    value={inputValue}
                    onChange={handleRawInputChange}
                    onFocus={() => setShowDayPicker(true)}
                    disabled={disabled}
                    onBlur={handleRawInputBlur}
                />
                {showDayPicker ?
                    <div className="rdp-overlay">
                        {withCustomView ? (
                            <CustomDatePicker
                                date={currentDate}
                                from={currentDate}
                                to={currentDate}
                                onDayClick={handleOnDayClick}
                            />
                        ) : (
                            <DayPicker
                                className="rdp-single"
                                toMonth={toMonth}
                                month={month}
                                onMonthChange={setMonth}
                                showOutsideDays={true}
                                selected={{
                                    from: currentDate,
                                    to: currentDate
                                }}
                                onDayClick={handleOnDayClick}
                                modifiers={{
                                    disabled: [
                                        ...disabledDays,
                                        {
                                            dayOfWeek: allowWeekends ? [] : [0, 6]
                                        },
                                        ...(maximumDate ? [{ after: maximumDate }] : []),
                                        ...(minimumDate ? [{ before: minimumDate }] : []),
                                    ]
                                }}
                                components={{
                                    Chevron: (props) => {
                                        if (props.orientation === "left") {
                                            return renderNavIcon();
                                        }
                                        return renderNavIcon();
                                    }
                                }}
                            />
                        )}
                    </div> : null}
            </div>
        </ClickOutside>
    )
}